// One year's file plan: its state, its document (generated from the plan,
// signed with E-IMZO through the ordinary signing flow when the project
// asks for that), the approve action with the reason it waits, and the
// cases grouped by the unit that keeps them.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, CheckCircle2, FileText, ListOrdered, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/modules/console/components/ConfirmDialog.jsx';
import { DocumentEditorModal } from '@/shared/ui/DocumentEditorModal.jsx';
import { FieldError } from '@/shared/ui/FieldError.jsx';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { records } from '../lib/api.js';
import { LIMITS, planProblems } from '../lib/validation.js';
import { PLAN_STATUS_TONE as STATUS_TONE, periodLabel } from '../lib/retention.js';
import { CaseDialog } from './CaseDialog.jsx';
import { CaseSheet } from './CaseSheet.jsx';

const DOCUMENT_LANGUAGE = { en: 'en', ru: 'ru', uz: 'uz', 'uz-Cyrl': 'uz-Cyrl' };

function PlanEditDialog({ open, onOpenChange, plan, onSave }) {
  const { t } = useTranslation('records');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const checks = useFieldErrors(planProblems({ title, note }));
  const { reset } = checks;
  useEffect(() => {
    if (open) {
      setTitle(plan?.title || '');
      setNote(plan?.note || '');
      reset();
    }
  }, [open, plan, reset]);
  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    try {
      const res = await onSave({ title: title.trim() || null, note: note.trim() || null });
      if (res && !res.success) checks.showServer(res, ['title', 'note']);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('plan.editTitle', { year: plan?.year })}</DialogTitle>
          <DialogDescription>{t('plan.editDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="plan-title">{t('plan.organisation')}</Label>
            <Input id="plan-title" maxLength={LIMITS.planTitle} value={title}
              onChange={(e) => { setTitle(e.target.value); checks.touch('title'); }} {...checks.describe('title', 'plan-edit')} />
            <FieldError id="plan-edit-title-error" message={checks.errors.title} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan-note">{t('plan.note')}</Label>
            <Textarea id="plan-note" rows={3} maxLength={LIMITS.note} value={note}
              onChange={(e) => { setNote(e.target.value); checks.touch('note'); }} {...checks.describe('note', 'plan-edit')} />
            <FieldError id="plan-edit-note-error" message={checks.errors.note} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button onClick={submit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PlanView({ planId, canManage, canReview = false, classes, onChanged, onDeleted }) {
  const { t, i18n } = useTranslation('records');
  const [plan, setPlan] = useState(null);
  const [caseForm, setCaseForm] = useState({ open: false, initial: null });
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'approve' | 'delete' | {case}
  const [busy, setBusy] = useState(null);
  const [opened, setOpened] = useState(null);
  const [openCase, setOpenCase] = useState(null);

  const load = useCallback(async () => {
    const res = await records.plan(planId);
    if (res.success) setPlan(res.response);
    else toast.error(res.message);
  }, [planId]);

  useEffect(() => {
    setPlan(null);
    load();
  }, [load]);

  const groups = useMemo(() => {
    const out = [];
    for (const item of plan?.items || []) {
      const last = out[out.length - 1];
      if (last && last.unit === (item.unit || null)) last.items.push(item);
      else out.push({ unit: item.unit || null, items: [item] });
    }
    return out;
  }, [plan]);

  if (!plan) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const draft = plan.status === 'draft';
  const closed = plan.status === 'closed';
  const refresh = async () => {
    await load();
    onChanged?.();
  };

  const saveCase = async (body) => {
    const editingCase = caseForm.initial;
    const res = editingCase ? await records.updateCase(editingCase.id, body) : await records.addCase(plan.id, body);
    if (res.success) {
      toast.success(t(editingCase ? 'case.saved' : 'case.added'));
      setCaseForm({ open: false, initial: null });
      refresh();
    }
    return res; // the dialog shows a refusal beside its field
  };
  const removeCase = async (item) => {
    const res = await records.deleteCase(item.id);
    if (res.success) {
      toast.success(t('case.deleted'));
      refresh();
    } else {
      toast.error(res.message);
    }
  };
  const savePlan = async (body) => {
    const res = await records.updatePlan(plan.id, body);
    if (res.success) {
      setEditing(false);
      refresh();
    }
    return res; // the dialog shows a refusal beside its field
  };
  const approve = async () => {
    const res = await records.approve(plan.id);
    if (res.success) {
      toast.success(t('plan.approved', { year: plan.year }));
      refresh();
    } else {
      toast.error(res.message);
    }
  };
  const makeInventory = async (kind) => {
    const res = await records.inventory({ year: plan.year, kind, language: DOCUMENT_LANGUAGE[i18n.language] || 'ru' });
    if (res.success) {
      toast.success(t('inventory.made', { count: res.response.cases }));
      setOpened({ id: res.response.file_id, name: res.response.name });
    } else {
      toast.error(res.message);
    }
  };
  const closePlan = async () => {
    const res = await records.closePlan(plan.id);
    if (res.success) {
      toast.success(t('plan.closedDone', { count: res.response.closed_cases }));
      refresh();
    } else {
      toast.error(res.message);
    }
  };
  const removePlan = async () => {
    const res = await records.deletePlan(plan.id);
    if (res.success) {
      toast.success(t('plan.deleted', { year: plan.year }));
      onDeleted?.();
    } else {
      toast.error(res.message);
    }
  };
  const makeDocument = async () => {
    setBusy('document');
    const res = await records.document(plan.id, { language: DOCUMENT_LANGUAGE[i18n.language] || 'ru' });
    setBusy(null);
    if (res.success) {
      toast.success(t('plan.documentMade'));
      setPlan((prev) => ({ ...prev, ...res.response }));
      onChanged?.();
    } else {
      toast.error(res.message);
    }
  };

  const documentName = t('plan.documentName', { year: plan.year });
  const canRegenerate = draft && ['none', 'missing', 'editable'].includes(plan.document_status);

  return (
    <div className="space-y-4" data-testid="plan-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground">{t('plan.heading', { year: plan.year })}</h2>
            <Badge variant="outline" className={STATUS_TONE[plan.status]} data-testid="plan-status">{t(`plan.statuses.${plan.status}`)}</Badge>
          </div>
          {plan.title && <p className="text-sm text-muted-foreground">{plan.title}</p>}
          {plan.note && <p className="mt-1 text-xs text-muted-foreground">{plan.note}</p>}
        </div>
        {(canManage || canReview) && (
          <div className="flex flex-wrap gap-2">
            {canManage && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" /> {t('actions.edit')}
              </Button>
            )}
            {canManage && draft && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setConfirm('delete')}>
                <Trash2 className="h-3.5 w-3.5" /> {t('plan.delete')}
              </Button>
            )}
            {canReview && plan.status !== 'draft' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="plan-inventory">
                    <ListOrdered className="h-3.5 w-3.5" /> {t('inventory.menu')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {['permanent', 'long', 'all'].map((kind) => (
                    <DropdownMenuItem key={kind} onSelect={() => makeInventory(kind)}>{t(`inventory.kinds.${kind}`)}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canManage && plan.status === 'approved' && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setConfirm('closePlan')} data-testid="plan-close">
                <Archive className="h-3.5 w-3.5" /> {t('plan.close')}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 p-3 text-sm" data-testid="plan-approval">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="flex-1">
          {plan.document_file_id && plan.document_status !== 'missing'
            ? t(`plan.document.${plan.document_status}`, { defaultValue: plan.document_status })
            : t('plan.document.none')}
          {draft && !plan.can_approve && plan.approve_blocked_by && (
            <span className="block text-xs text-muted-foreground" data-testid="plan-blocked">{t(`plan.blocked.${plan.approve_blocked_by}`)}</span>
          )}
          {plan.status !== 'draft' && plan.approved_at && (
            <span className="block text-xs text-muted-foreground">{t('plan.approvedOn', { date: new Date(plan.approved_at).toLocaleDateString(i18n.language) })}</span>
          )}
        </span>
        {plan.document_file_id && plan.document_status !== 'missing' && (
          <Button variant="outline" size="sm" onClick={() => setOpened({ id: plan.document_file_id, name: documentName })}>{t('plan.openDocument')}</Button>
        )}
        {canManage && canRegenerate && (
          <Button variant="outline" size="sm" onClick={makeDocument} disabled={busy === 'document' || plan.cases === 0} data-testid="plan-make-document">
            {busy === 'document' && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {t(plan.document_file_id ? 'plan.remakeDocument' : 'plan.makeDocument')}
          </Button>
        )}
        {canManage && draft && (
          <Button size="sm" className="gap-1.5" onClick={() => setConfirm('approve')} disabled={!plan.can_approve} data-testid="plan-approve">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t('plan.approve')}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{t('plan.cases', { count: plan.cases })}</h3>
        {canManage && !closed && (
          <Button size="sm" className="gap-1.5" onClick={() => setCaseForm({ open: true, initial: null })} data-testid="case-add">
            <Plus className="h-3.5 w-3.5" /> {t('case.add')}
          </Button>
        )}
      </div>

      {plan.items.length === 0 ? (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">{t('plan.noCases')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t('case.index')}</th>
                <th className="px-3 py-2">{t('case.title')}</th>
                <th className="px-3 py-2">{t('case.retention')}</th>
                <th className="px-3 py-2">{t('case.note')}</th>
                {canManage && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => [
                <tr key={`unit-${group.unit}-${group.items[0].id}`} className="border-t bg-muted/30">
                  <td colSpan={canManage ? 5 : 4} className="px-3 py-1.5 text-xs font-semibold text-foreground">{group.unit || t('case.noUnit')}</td>
                </tr>,
                ...group.items.map((item) => (
                  <tr key={item.id} className="border-t" data-testid={`case-row-${item.case_index}`}>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{item.case_index}</td>
                    <td className="px-3 py-2">
                      <button type="button" className="text-left hover:underline" onClick={() => setOpenCase(item.id)} data-testid={`case-open-${item.case_index}`}>
                        {item.title}
                      </button>
                      {item.status !== 'open' && <span className="ml-2 text-xs text-muted-foreground">{t(`case.statuses.${item.status}`)}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      {periodLabel(item.retention, t)}
                      <span className="block text-muted-foreground">{t('case.article', { article: item.retention.article })}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{item.note}</td>
                    {canManage && (
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {!closed && item.status === 'open' && (
                          <Button variant="ghost" size="icon" aria-label={t('actions.edit')} onClick={() => setCaseForm({ open: true, initial: item })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {draft && (
                          <Button variant="ghost" size="icon" aria-label={t('actions.delete')} onClick={() => setConfirm({ case: item })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}

      <CaseDialog
        open={caseForm.open}
        onOpenChange={(open) => setCaseForm((prev) => ({ ...prev, open }))}
        initial={caseForm.initial}
        classes={classes}
        onSave={saveCase}
      />
      <PlanEditDialog open={editing} onOpenChange={setEditing} plan={plan} onSave={savePlan} />
      <ConfirmDialog
        open={confirm === 'approve'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t('plan.approveTitle', { year: plan.year })}
        description={t('plan.approveDescription', { count: plan.cases })}
        confirmLabel={t('plan.approve')}
        onConfirm={approve}
      />
      <ConfirmDialog
        open={confirm === 'closePlan'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t('plan.closeTitle', { year: plan.year })}
        description={t('plan.closeDescription')}
        confirmLabel={t('plan.close')}
        onConfirm={closePlan}
      />
      <CaseSheet caseId={openCase} canManage={canManage} canReview={canReview} onOpenChange={(o) => { if (!o) setOpenCase(null); }} onChanged={refresh} />
      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t('plan.deleteTitle', { year: plan.year })}
        description={t('plan.deleteDescription')}
        confirmLabel={t('plan.delete')}
        destructive
        onConfirm={removePlan}
      />
      <ConfirmDialog
        open={Boolean(confirm?.case)}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t('case.deleteTitle', { index: confirm?.case?.case_index })}
        confirmLabel={t('actions.delete')}
        destructive
        onConfirm={() => removeCase(confirm.case)}
      />
      {opened && (
        <DocumentEditorModal open doc={opened} onClose={() => { setOpened(null); refresh(); }} onSave={refresh} />
      )}
    </div>
  );
}
